import React, { useState } from 'react';
import { Form, Input, Button, Upload, message, Card, Typography, Space, Select } from 'antd';
import { UploadOutlined, SaveOutlined } from '@ant-design/icons';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { submitDriverData } from '../../services/api';
import './styles.css';

const { Title } = Typography;
const { Option } = Select;

interface FormValues {
  driverName: string;
  driverPhone: string;
  driverLicense: string;
  vehicleNumber: string;
  vehicleType: string;
  idCardFront: string;
  idCardBack: string;
  driverLicenseImage: string;
  vehicleLicenseImage: string;
}

// Define upload options interface
interface UploadOptions {
  file: File;
  onSuccess: (response: any, file: File) => void;
  onError: (error: any, response?: any, file?: File) => void;
}

const DataSubmissionPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  // Validation schema using Yup
  const validationSchema = Yup.object({
    driverName: Yup.string().required('请输入司机姓名'),
    driverPhone: Yup.string()
      .matches(/^1[3-9]\d{9}$/, '请输入有效的手机号码')
      .required('请输入手机号码'),
    driverLicense: Yup.string().required('请输入驾驶证号'),
    vehicleNumber: Yup.string().required('请输入车牌号'),
    vehicleType: Yup.string().required('请选择车辆类型'),
    idCardFront: Yup.string().required('请上传身份证正面照片'),
    idCardBack: Yup.string().required('请上传身份证背面照片'),
    driverLicenseImage: Yup.string().required('请上传驾驶证照片'),
    vehicleLicenseImage: Yup.string().required('请上传行驶证照片'),
  });

  // Initialize formik
  const formik = useFormik<FormValues>({
    initialValues: {
      driverName: '',
      driverPhone: '',
      driverLicense: '',
      vehicleNumber: '',
      vehicleType: '',
      idCardFront: '',
      idCardBack: '',
      driverLicenseImage: '',
      vehicleLicenseImage: '',
    },
    validationSchema,
    onSubmit: async (values: FormValues) => {
      try {
        setLoading(true);
        
        // Submit data to API
        const response = await submitDriverData(values);
        
        if (response.data.code === 0) {
          message.success('数据提交成功！');
          formik.resetForm();
        } else {
          message.error(response.data.msg || '提交失败，请重试');
        }
      } catch (error) {
        console.error('提交错误:', error);
        message.error('提交失败，请检查网络连接后重试');
      } finally {
        setLoading(false);
      }
    },
  });

  // Function to convert file to BASE64
  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  // Custom upload button that converts image to BASE64
  const customUpload = async (options: UploadOptions, fieldName: keyof FormValues) => {
    const { file, onSuccess, onError } = options;
    
    try {
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        message.error('文件大小不能超过5MB');
        onError('File size exceeded');
        return;
      }
      
      // Check file type
      if (!file.type.includes('image/')) {
        message.error('只能上传图片文件');
        onError('Invalid file type');
        return;
      }
      
      // Convert to BASE64
      const base64 = await convertToBase64(file);
      
      // Update formik value
      formik.setFieldValue(fieldName, base64);
      onSuccess('ok', file);
      message.success(`${file.name} 上传成功`);
    } catch (err) {
      console.error('文件上传错误:', err);
      message.error(`${file.name} 上传失败`);
      onError('Upload failed');
    }
  };

  return (
    <div className="submission-container">
      <Card className="submission-card">
        <Title level={2} className="submission-title">司机信息提交</Title>
        
        <Form form={form} layout="vertical" onFinish={formik.handleSubmit}>
          <Form.Item 
            label="司机姓名" 
            validateStatus={formik.touched.driverName && formik.errors.driverName ? 'error' : ''}
            help={formik.touched.driverName && formik.errors.driverName}
          >
            <Input
              name="driverName"
              value={formik.values.driverName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="请输入司机姓名"
            />
          </Form.Item>
          
          <Form.Item 
            label="手机号码" 
            validateStatus={formik.touched.driverPhone && formik.errors.driverPhone ? 'error' : ''}
            help={formik.touched.driverPhone && formik.errors.driverPhone}
          >
            <Input
              name="driverPhone"
              value={formik.values.driverPhone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="请输入手机号码"
            />
          </Form.Item>
          
          <Form.Item 
            label="驾驶证号" 
            validateStatus={formik.touched.driverLicense && formik.errors.driverLicense ? 'error' : ''}
            help={formik.touched.driverLicense && formik.errors.driverLicense}
          >
            <Input
              name="driverLicense"
              value={formik.values.driverLicense}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="请输入驾驶证号"
            />
          </Form.Item>
          
          <Form.Item 
            label="车牌号" 
            validateStatus={formik.touched.vehicleNumber && formik.errors.vehicleNumber ? 'error' : ''}
            help={formik.touched.vehicleNumber && formik.errors.vehicleNumber}
          >
            <Input
              name="vehicleNumber"
              value={formik.values.vehicleNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="请输入车牌号"
            />
          </Form.Item>
          
          <Form.Item 
            label="车辆类型" 
            validateStatus={formik.touched.vehicleType && formik.errors.vehicleType ? 'error' : ''}
            help={formik.touched.vehicleType && formik.errors.vehicleType}
          >
            <Select
              id="vehicleType"
              value={formik.values.vehicleType}
              onChange={(value: string) => formik.setFieldValue('vehicleType', value)}
              onBlur={() => formik.setFieldTouched('vehicleType', true)}
              placeholder="请选择车辆类型"
            >
              <Option value="小型汽车">小型汽车</Option>
              <Option value="中型汽车">中型汽车</Option>
              <Option value="大型汽车">大型汽车</Option>
              <Option value="货车">货车</Option>
              <Option value="其他">其他</Option>
            </Select>
          </Form.Item>
          
          <Form.Item 
            label="身份证正面照片" 
            validateStatus={formik.touched.idCardFront && formik.errors.idCardFront ? 'error' : ''}
            help={formik.touched.idCardFront && formik.errors.idCardFront}
          >
            <Upload
              customRequest={(options: any) => customUpload(options, 'idCardFront')}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />}>上传身份证正面照片</Button>
            </Upload>
            {formik.values.idCardFront && (
              <div className="preview-image">
                <img src={formik.values.idCardFront} alt="身份证正面" />
              </div>
            )}
          </Form.Item>
          
          <Form.Item 
            label="身份证背面照片" 
            validateStatus={formik.touched.idCardBack && formik.errors.idCardBack ? 'error' : ''}
            help={formik.touched.idCardBack && formik.errors.idCardBack}
          >
            <Upload
              customRequest={(options: any) => customUpload(options, 'idCardBack')}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />}>上传身份证背面照片</Button>
            </Upload>
            {formik.values.idCardBack && (
              <div className="preview-image">
                <img src={formik.values.idCardBack} alt="身份证背面" />
              </div>
            )}
          </Form.Item>
          
          <Form.Item 
            label="驾驶证照片" 
            validateStatus={formik.touched.driverLicenseImage && formik.errors.driverLicenseImage ? 'error' : ''}
            help={formik.touched.driverLicenseImage && formik.errors.driverLicenseImage}
          >
            <Upload
              customRequest={(options: any) => customUpload(options, 'driverLicenseImage')}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />}>上传驾驶证照片</Button>
            </Upload>
            {formik.values.driverLicenseImage && (
              <div className="preview-image">
                <img src={formik.values.driverLicenseImage} alt="驾驶证" />
              </div>
            )}
          </Form.Item>
          
          <Form.Item 
            label="行驶证照片" 
            validateStatus={formik.touched.vehicleLicenseImage && formik.errors.vehicleLicenseImage ? 'error' : ''}
            help={formik.touched.vehicleLicenseImage && formik.errors.vehicleLicenseImage}
          >
            <Upload
              customRequest={(options: any) => customUpload(options, 'vehicleLicenseImage')}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />}>上传行驶证照片</Button>
            </Upload>
            {formik.values.vehicleLicenseImage && (
              <div className="preview-image">
                <img src={formik.values.vehicleLicenseImage} alt="行驶证" />
              </div>
            )}
          </Form.Item>
          
          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              icon={<SaveOutlined />}
              size="large"
              block
            >
              提交信息
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default DataSubmissionPage; 